import { useCallback, useRef, useState, RefObject, useEffect } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import { UniqueIdentifier } from '@dnd-kit/core'

export type Sortable<T> = { id: number } & T

const toEdittable = <T,>(id: RefObject<number>, original: T) => {
	id.current = id.current + 1
	return { ...original, id: id.current }
}

type useSortableListProps<T> = {
	list?: T[]
}
export const useSortableList = <T,>({
	list
}: useSortableListProps<T>) => {
  const sortableId = useRef<number>(0)
  const resetSortableId = () => { sortableId.current = 0 }
  const [sortableList, setSortableList] = useState<Sortable<T>[]>([])
	
	useEffect(
		() => {
			setSortableList(list?.map((r) => toEdittable(sortableId, r)) || [])
		},
		[list]
	)
	const onLoad = useCallback(
		(list: T[]) => {
			resetSortableId()
			setSortableList(list.map((r) => toEdittable(sortableId, r)))
		},
		[setSortableList]
	)
	const addRecord = useCallback(
		(data: T) => {
			const newItems = [...sortableList].concat([toEdittable(sortableId, data)])
			setSortableList(newItems)
			return newItems
		},
		[sortableList]
	)
	const editRecord = useCallback(
		(id: number, data: T) => {
			const newItems = sortableList.map((d) => d.id === id ? { ...data, id } : d)
			setSortableList(newItems)
			return newItems
		},
		[sortableList]
	)
	const removeRecord = useCallback(
		(id: number) => {
			const newItems = sortableList.filter((item) => item.id !== id)
			setSortableList(newItems)
			return newItems
		},
		[sortableList]
	)
	const moveRecord = useCallback(
		(activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
      const newItems = arrayMove(
        sortableList,
        sortableList.findIndex((item) => item.id === activeId),
        sortableList.findIndex((item) => item.id === overId)
      )
			setSortableList(newItems)
			return newItems
		},
		[sortableList]
	)
	return {
		sortableList,
		onLoad,
    setSortableList,
    addRecord,
		editRecord,
		removeRecord,
		moveRecord
	}
}